<!-- Swipe.vue -->
<template>
  <div class="swipe-container" ref="swipeContainer">
    <div class="swipe-wrapper" :style="wrapperStyle" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd" @mousedown="onMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp">
      <slot></slot>
    </div>

    <!-- Navigation dots -->
    <div class="swipe-dots" v-if="showDots && direction === 'horizontal'">
      <span v-for="n in getChildrenCount()" :key="n" class="dot" :class="{ active: n - 1 === currentIndex }" @click="goTo(n - 1)"></span>
    </div>

    <!-- Navigation arrows -->
    <div class="swipe-arrows" v-if="showArrows">
      <button class="arrow prev" :disabled="disabled || (currentIndex === 0 && !loop)" @click="prev">
        {{ direction === 'horizontal' ? '←' : '↑' }}
      </button>
      <button class="arrow next" :disabled="disabled || (currentIndex === getChildrenCount() - 1 && !loop)" @click="next">
        {{ direction === 'horizontal' ? '→' : '↓' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Swipe',
  props: {
    // Direction of swipe: 'horizontal' or 'vertical'
    direction: {
      type: String,
      default: 'horizontal',
      validator: (value) => ['horizontal', 'vertical'].includes(value),
    },
    // Whether to enable loop scrolling
    loop: {
      type: Boolean,
      default: false,
    },
    // Auto play interval in ms (0 to disable)
    autoplay: {
      type: Number,
      default: 0,
    },
    // Transition duration in ms
    duration: {
      type: Number,
      default: 300,
    },
    // Threshold for triggering swipe (0-1)
    threshold: {
      type: Number,
      default: 0.2,
    },
    // Minimum velocity for swipe detection
    minVelocity: {
      type: Number,
      default: 0.3,
    },
    // Minimum distance for swipe detection (in pixels)
    minDistance: {
      type: Number,
      default: 30,
    },
    // Maximum time for tap detection (ms)
    tapThreshold: {
      type: Number,
      default: 100,
    },
    // Throttle time for swipe events (ms)
    throttleTime: {
      type: Number,
      default: 10,
    },
    // Debounce time for slide changes (ms)
    debounceTime: {
      type: Number,
      default: 10,
    },
    // Show navigation dots
    showDots: {
      type: Boolean,
      default: true,
    },
    // Show navigation arrows
    showArrows: {
      type: Boolean,
      default: true,
    },
    // Initial index
    initialIndex: {
      type: Number,
      default: 0,
    },
    // Disable swiping
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentIndex: this.initialIndex,
      // childrenCount: 0,
      startPos: { x: 0, y: 0 },
      movePos: { x: 0, y: 0 },
      endPos: { x: 0, y: 0 },
      startTime: 0,
      isMoving: false,
      isScrolling: false,
      offset: 0,
      autoplayTimer: null,
      windowWidth: 0,
      windowHeight: 0,
      // Track if user has moved beyond tap threshold
      hasMoved: false,
      // Throttling and debouncing
      lastSwipeTime: 0,
      lastChangeTime: 0,
      changeTimer: null,
    };
  },
  computed: {
    wrapperStyle() {
      const translateValue = -this.currentIndex * 100 + this.offset;

      if (this.direction === 'horizontal') {
        return {
          transform: `translate3d(${translateValue}%, 0, 0)`,
          transition: this.isMoving ? 'none' : `transform ${this.duration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
          width: `${100}%`,
          flexDirection: 'row',
        };
      } else {
        return {
          transform: `translate3d(0, ${translateValue}%, 0)`,
          transition: this.isMoving ? 'none' : `transform ${this.duration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
          height: `${100}%`,
          flexDirection: 'column',
        };
      }
    },
    childrenCount() {
      console.log('this.$children ', this.$children);
      // return this.$slots.default ? this.$slots.default.length : 0;
      return this.$children ? this.$children.length : 0;
    },

    // Get the dimension based on direction
    containerDimension() {
      return this.direction === 'horizontal' ? this.windowWidth : this.windowHeight;
    },
  },
  methods: {
    getChildrenCount() {
      return this.$slots.default ? this.$slots.default.length : 0;
    },
    // 锁定页面滚动
    lockScroll() {
      if (this.isScrollLocked) return;

      this.startScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.startScrollTop}px`;
      document.body.style.width = '100%';
      this.isScrollLocked = true;
    },

    // 解锁页面滚动
    unlockScroll() {
      if (!this.isScrollLocked) return;

      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      this.isScrollLocked = false;
    },

    // Throttle function for swipe events
    throttleSwipe(func, delay) {
      const now = Date.now();
      if (now - this.lastSwipeTime >= delay) {
        this.lastSwipeTime = now;
        return func();
      }
      return false;
    },

    // Debounce function for slide changes
    debounceChange(func, delay) {
      if (this.changeTimer) {
        clearTimeout(this.changeTimer);
      }
      this.changeTimer = setTimeout(() => {
        const now = Date.now();
        if (now - this.lastChangeTime >= delay) {
          this.lastChangeTime = now;
          func();
        }
      }, delay);
    },

    // Handle touch start
    onTouchStart(e) {
      if (this.disabled) return;
      this.startPos = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      this.startTime = Date.now();
      this.isMoving = true;
      this.isScrolling = false;
      this.hasMoved = false;

      // 锁定页面滚动
      this.lockScroll();

      // Clear autoplay timer
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
      }
    },

    // Handle touch move
    onTouchMove(e) {
      console.log('onTouchMove:', this.isMoving);
      if (this.disabled || !this.isMoving) return;

      // Throttle touch move events
      this.throttleSwipe(() => {
        this.movePos = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };

        // Calculate movement
        const diffX = this.movePos.x - this.startPos.x;
        const diffY = this.movePos.y - this.startPos.y;

        // Check if user has moved beyond tap threshold
        if (!this.hasMoved) {
          const distance = Math.sqrt(diffX * diffX + diffY * diffY);
          if (distance > 5) {
            this.hasMoved = true;
          }
        }

        // Determine if user is trying to scroll in the opposite direction
        if (!this.isScrolling) {
          if (this.direction === 'horizontal') {
            this.isScrolling = Math.abs(diffY) > Math.abs(diffX);
          } else {
            this.isScrolling = Math.abs(diffX) > Math.abs(diffY);
          }
        }

        // Prevent swiping in the main direction if scrolling detected
        if (this.isScrolling) {
          this.isMoving = false;
          return;
        }

        // Prevent browser behavior
        e.preventDefault();

        // Calculate offset percentage based on direction
        const diff = this.direction === 'horizontal' ? diffX : diffY;
        this.offset = (diff / this.containerDimension) * 100;

        // If not looping, add resistance at edges
        if (!this.loop) {
          if ((this.currentIndex === 0 && this.offset > 0) || (this.currentIndex === this.$children.length - 1 && this.offset < 0)) {
            this.offset *= 0.2; // Add resistance
          }
        }
      }, this.throttleTime);
    },

    // Handle touch end
    onTouchEnd() {
      if (this.disabled || !this.isMoving) return;

      this.endPos = { ...this.movePos };
      this.isMoving = false;

      // 解锁页面滚动
      this.unlockScroll();

      // Check if it's a tap (quick press without significant movement)
      const deltaTime = Date.now() - this.startTime;
      if (deltaTime < this.tapThreshold && !this.hasMoved) {
        // This is a tap, not a swipe - do nothing
        this.offset = 0;
        return;
      }

      // Calculate swipe speed
      const diff = this.direction === 'horizontal' ? this.endPos.x - this.startPos.x : this.endPos.y - this.startPos.y;
      const velocity = Math.abs(diff) / deltaTime;

      // Determine if it's a valid swipe
      const isValidSwipe = Math.abs(diff) > this.minDistance || velocity > this.minVelocity || Math.abs(diff) > this.containerDimension * this.threshold;

      if (isValidSwipe) {
        // console.log('isValidSwipe', this.currentIndex, this.childrenCount);
        // Add boundary checks
        if (diff < 0) {
          // Swiping to next slide
          if (this.currentIndex < this.getChildrenCount() - 1 || this.loop) {
            this.next();
          } else {
            this.offset = 0; // Stay in place if at last slide and no loop
          }
        } else {
          // Swiping to previous slide
          if (this.currentIndex > 0 || this.loop) {
            this.prev();
          } else {
            this.offset = 0; // Stay in place if at first slide and no loop
          }
        }
      } else {
        // Reset to current slide
        this.offset = 0;
      }

      // Restart autoplay if enabled
      this.restartAutoplay();
    },

    // Handle mouse down
    onMouseDown(e) {
      if (this.disabled) return;

      this.startPos = {
        x: e.clientX,
        y: e.clientY,
      };
      this.startTime = Date.now();
      this.isMoving = true;
      this.isScrolling = false;
      this.hasMoved = false;

      // Clear autoplay timer
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
      }
    },

    // Handle mouse move
    onMouseMove(e) {
      if (this.disabled || !this.isMoving) return;

      // Throttle mouse move events
      this.throttleSwipe(() => {
        this.movePos = {
          x: e.clientX,
          y: e.clientY,
        };

        // Calculate movement
        const diffX = this.movePos.x - this.startPos.x;
        const diffY = this.movePos.y - this.startPos.y;

        // Check if user has moved beyond tap threshold
        if (!this.hasMoved) {
          const distance = Math.sqrt(diffX * diffX + diffY * diffY);
          if (distance > 5) {
            this.hasMoved = true;
          }
        }

        // Determine if user is trying to scroll in the opposite direction
        if (!this.isScrolling) {
          if (this.direction === 'horizontal') {
            this.isScrolling = Math.abs(diffY) > Math.abs(diffX);
          } else {
            this.isScrolling = Math.abs(diffX) > Math.abs(diffY);
          }
        }

        // Prevent swiping in the main direction if scrolling detected
        if (this.isScrolling) {
          this.isMoving = false;
          return;
        }

        // Calculate offset percentage based on direction
        const diff = this.direction === 'horizontal' ? diffX : diffY;
        this.offset = (diff / this.containerDimension) * 100;

        // If not looping, add resistance at edges
        if (!this.loop) {
          if ((this.currentIndex === 0 && this.offset > 0) || (this.currentIndex === this.getChildrenCount() - 1 && this.offset < 0)) {
            this.offset *= 0.2; // Add resistance
          }
        }
      }, this.throttleTime);
    },

    // Handle mouse up
    onMouseUp() {
      if (this.disabled || !this.isMoving) return;

      this.endPos = { ...this.movePos };
      this.isMoving = false;

      // Check if it's a tap (quick press without significant movement)
      const deltaTime = Date.now() - this.startTime;
      if (deltaTime < this.tapThreshold && !this.hasMoved) {
        // This is a tap, not a swipe - do nothing
        this.offset = 0;
        return;
      }

      // Calculate swipe speed
      const diff = this.direction === 'horizontal' ? this.endPos.x - this.startPos.x : this.endPos.y - this.startPos.y;
      const velocity = Math.abs(diff) / deltaTime;

      // Determine if it's a valid swipe
      const isValidSwipe = Math.abs(diff) > this.minDistance || velocity > this.minVelocity || Math.abs(diff) > this.containerDimension * this.threshold;

      if (isValidSwipe) {
        // Add boundary checks
        if (diff < 0) {
          // Swiping to next slide
          if (this.currentIndex < this.getChildrenCount() - 1 || this.loop) {
            this.next();
          } else {
            this.offset = 0; // Stay in place if at last slide and no loop
          }
        } else {
          // Swiping to previous slide
          if (this.currentIndex > 0 || this.loop) {
            this.prev();
          } else {
            this.offset = 0; // Stay in place if at first slide and no loop
          }
        }
      } else {
        // Reset to current slide
        this.offset = 0;
      }

      // Restart autoplay if enabled
      this.restartAutoplay();
    },

    // Go to next slide
    next() {
      if (this.disabled) return;

      // Debounce slide changes
      const currentIndex = this.currentIndex;
      this.debounceChange(() => {
        if (this.loop) {
          this.currentIndex = (this.currentIndex + 1) % this.getChildrenCount();
        } else {
          this.currentIndex = Math.min(this.currentIndex + 1, this.getChildrenCount() - 1);
        }

        this.offset = 0;
        if (currentIndex != this.currentIndex) {
          this.$emit('change', this.currentIndex);
        }
      }, this.debounceTime);
    },

    // Go to previous slide
    prev() {
      if (this.disabled) return;

      // Debounce slide changes
      const currentIndex = this.currentIndex;
      this.debounceChange(() => {
        if (this.loop) {
          this.currentIndex = (this.currentIndex - 1 + this.getChildrenCount()) % this.getChildrenCount();
        } else {
          this.currentIndex = Math.max(this.currentIndex - 1, 0);
        }

        this.offset = 0;
        if (currentIndex != this.currentIndex) {
          this.$emit('change', this.currentIndex);
        }
      }, this.debounceTime);
    },

    // Go to specific slide
    swipeTo(index) {
      if (this.disabled || index < 0 || index >= this.getChildrenCount()) return;

      // Debounce slide changes
      this.debounceChange(() => {
        this.currentIndex = index;
        this.offset = 0;
        this.$emit('change', this.currentIndex);
      }, this.debounceTime);

      // Restart autoplay if enabled
      this.restartAutoplay();
    },

    // Start autoplay
    startAutoplay() {
      if (this.autoplay <= 0) return;

      this.autoplayTimer = setTimeout(() => {
        this.next();
        this.startAutoplay();
      }, this.autoplay);
    },

    // Restart autoplay
    restartAutoplay() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
      }
      this.startAutoplay();
    },

    // Update window dimensions
    updateWindowDimensions() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
  },
  mounted() {
    this.updateWindowDimensions();

    // Add event listeners
    window.addEventListener('resize', this.updateWindowDimensions);

    // Start autoplay if enabled
    this.startAutoplay();
  },
  beforeUnmount() {
    // Clear timers
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
    }

    if (this.changeTimer) {
      clearTimeout(this.changeTimer);
    }

    // Remove event listeners
    window.removeEventListener('resize', this.updateWindowDimensions);
  },
  watch: {
    // Watch for disabled state changes
    disabled(newVal) {
      if (newVal) {
        // Reset any ongoing movements
        this.isMoving = false;
        this.offset = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.swipe-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: pan-y; // For better touch performance on horizontal swipe
  // 添加以下属性防止抖动
  -webkit-overflow-scrolling: touch;
  will-change: transform;
}

.swipe-container.vertical {
  touch-action: pan-x; // For better touch performance on vertical swipe
  // 对于垂直滑动，完全禁用默认的触摸行为
  overflow: visible; // 让内容可以超出容器
}

.swipe-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
  will-change: transform;
  backface-visibility: hidden; // Improve rendering performance
  // 添加硬件加速
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

// 确保子元素不会影响触摸行为
.swipe-wrapper ::v-deep(.swipe-item) {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  cursor: grab;
  // 防止子元素的触摸事件冒泡影响
  -webkit-overflow-scrolling: touch;
  overflow: hidden;
}

.swipe-wrapper ::v-deep(.swipe-item):active {
  cursor: grabbing;
}

.swipe-dots {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  z-index: 10;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  margin: 0 5px;
  cursor: pointer;
  transition: background 0.3s;

  &.active {
    background: #fff;
  }
}

.swipe-arrows {
  position: absolute;
  display: flex;
  z-index: 10;
}

/* Horizontal arrows */
.swipe-arrows.horizontal {
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  justify-content: space-between;
  padding: 0 20px;
}

/* Vertical arrows */
.swipe-arrows.vertical {
  top: 0;
  bottom: 0;
  left: 20px;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 0;
}

.arrow {
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  user-select: none; // Prevent text selection during swipe

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// 添加 iOS 特定的样式
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .swipe-container {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
}
</style>
